import { useState, useEffect, useCallback } from 'react'

// =======================================================================
// useDraftPersistence — the ONE generic mechanism for preserving unsaved
// estimator input across a dedicated engineering module's modal being
// closed and reopened (React unmount/remount destroys local useState by
// design; this hook survives it via sessionStorage).
//
// This is intentionally reusable by ANY current or future dedicated
// module (Rebar Slab, Rebar Beam, Column, Footing, Wall, Masonry,
// Formwork, ...) — no module-specific persistence logic should ever be
// written again. A module only needs to call this hook with its own
// unique draftKey and initial state shape.
//
// Drafts are explicitly NOT the same concept as a saved Output/History
// entry: they hold transient estimator input only, are cleared the
// moment a Save succeeds, and never touch quantity_source /
// resource_quantity_output / any SSOT mechanism.
// =======================================================================

export function useDraftPersistence(draftKey, initialState) {
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(draftKey)
      return raw ? JSON.parse(raw) : initialState
    } catch {
      return initialState
    }
  })

  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(state))
    } catch {
      // sessionStorage unavailable/full — draft persistence degrades
      // silently to "no draft", never breaks the module itself.
    }
  }, [draftKey, state])

  const clearDraft = useCallback(() => {
    try { sessionStorage.removeItem(draftKey) } catch { /* ignore */ }
  }, [draftKey])

  const hasDraft = useCallback(() => {
    try { return sessionStorage.getItem(draftKey) != null } catch { return false }
  }, [draftKey])

  return [state, setState, clearDraft, hasDraft]
}
